import { Check, Copy, RotateCcw } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { ScrollView } from 'react-native';

import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FORMAT_RESPONSE } from '@/constants/formatResponse';
import useClipboard from '@/hooks/useClipboard';
import { useToaster } from '@/hooks/useToaster';
import { segmentClient } from '@/util/analytics';

type ViewMode = 'formatted' | 'original';

function formatResponseAppendA11yLabel(viewMode: ViewMode, appended: boolean): string {
  if (viewMode === 'original') {
    return 'Switch to formatted view to append';
  }

  if (appended) {
    return 'Response appended to message';
  }

  return 'Append response to message';
}

function formatResponseCopyA11yLabel(viewMode: ViewMode, copied: boolean): string {
  if (viewMode === 'original') {
    return 'Switch to formatted view to copy';
  }

  if (copied) {
    return 'Copied to clipboard';
  }

  return 'Copy to clipboard';
}

type FormattedResponseDisplayProps = {
  formattedResponse: string;
  originalResponse?: string | null;
  onReset: () => void;
  onAppendToChat: (text: string) => void;
  wrapText: (text: string) => string;
};

export function FormattedResponseDisplay({
  formattedResponse,
  originalResponse,
  onReset,
  onAppendToChat,
  wrapText,
}: FormattedResponseDisplayProps) {
  const { copyToClipboard } = useClipboard();

  const toast = useToaster();

  const [copied, setCopied] = useState(false);

  const [appended, setAppended] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>('formatted');

  const appendAccessibilityLabel = formatResponseAppendA11yLabel(viewMode, appended);

  const copyAccessibilityLabel = formatResponseCopyA11yLabel(viewMode, copied);

  const handleCopy = useCallback(() => {
    copyToClipboard(formattedResponse);

    segmentClient.track(ANALYTICS_EVENTS.FORMAT_RESPONSE_COPIED, {
      response_length: formattedResponse.length,
    });

    setCopied(true);

    setTimeout(() => setCopied(false), FORMAT_RESPONSE.BUTTON_RESET_DELAY_MS);
  }, [copyToClipboard, formattedResponse]);

  const handleAppend = useCallback(() => {
    const textToAppend = wrapText(formattedResponse);

    onAppendToChat(textToAppend);

    segmentClient.track(ANALYTICS_EVENTS.FORMAT_RESPONSE_APPENDED, {
      response_length: textToAppend.length,
    });

    toast.showSuccess({
      title: 'Added to message',
      message: 'Formatted response has been added to your draft',
    });

    setAppended(true);

    setTimeout(() => setAppended(false), FORMAT_RESPONSE.BUTTON_RESET_DELAY_MS);
  }, [formattedResponse, onAppendToChat, toast, wrapText]);

  const handleToggleView = useCallback(() => {
    const newMode = viewMode === 'formatted' ? 'original' : 'formatted';

    setViewMode(newMode);

    if (newMode === 'original') {
      segmentClient.track(ANALYTICS_EVENTS.FORMAT_RESPONSE_COMPARISON_VIEWED);
    }
  }, [viewMode]);

  const hasOriginal = !!originalResponse;

  return (
    <VStack className="flex-1" space="sm">
      {hasOriginal && (
        <HStack space="xs" className="justify-end">
          <Button
            size="xs"
            variant={viewMode === 'formatted' ? 'solid' : 'outline'}
            onPress={() => viewMode !== 'formatted' && handleToggleView()}
            className="px-3">
            <ButtonText size="xs">Formatted</ButtonText>
          </Button>
          <Button
            size="xs"
            variant={viewMode === 'original' ? 'solid' : 'outline'}
            onPress={() => viewMode !== 'original' && handleToggleView()}
            className="px-3">
            <ButtonText size="xs">Original</ButtonText>
          </Button>
        </HStack>
      )}

      {viewMode === 'original' && originalResponse ? (
        <ScrollView className="flex-1 rounded-lg border border-outline-200 bg-background-100">
          <VStack className="p-3">
            <Text size="sm" className="select-text text-typography-700">
              {originalResponse}
            </Text>
          </VStack>
        </ScrollView>
      ) : (
        <ScrollView className="flex-1 rounded-lg border border-outline-200 bg-background-50">
          <Pressable onPress={handleCopy} className="p-3">
            <Text size="sm" className="select-text text-typography-900">
              {formattedResponse}
            </Text>
          </Pressable>
        </ScrollView>
      )}

      <HStack space="sm">
        <Button
          size="sm"
          onPress={handleAppend}
          className="flex-1"
          disabled={viewMode === 'original'}
          accessibilityLabel={appendAccessibilityLabel}>
          <ButtonText>{appended ? 'Appended!' : 'Append'}</ButtonText>
        </Button>

        <Button
          variant="outline"
          size="sm"
          onPress={handleCopy}
          disabled={viewMode === 'original'}
          accessibilityLabel={copyAccessibilityLabel}>
          <Icon as={copied ? Check : Copy} size="sm" />
        </Button>

        <Button
          variant="outline"
          size="sm"
          onPress={onReset}
          accessibilityLabel="Reset and format again">
          <Icon as={RotateCcw} size="sm" />
        </Button>
      </HStack>
    </VStack>
  );
}
